import { Inject, Injectable } from "@angular/core";
import { Manufacturer } from "../../../../shared/models/manufacturer";
import { Z2DataMatch } from "../../../../shared/models/dataProviders/z2data";
import { doc2Model } from "../api.service";
import { AppType, APP_TYPE } from "../app.type";
import { ManufacturerService } from "../manufacturer/manufacturer.service";
import { ExternalDataFilterServiceType } from "./external-data-filter.service.type";
import { BehaviorSubject } from "rxjs";
import { DataProvider } from "../../../../shared/constants/dataProvider";

@Injectable()
export class ExternalDataFilterService implements ExternalDataFilterServiceType {
  currentMatch: Z2DataMatch[] = [];
  selectedMatch: Z2DataMatch = new Z2DataMatch();
  matchesCopy: Z2DataMatch[] = [];
  matches: Z2DataMatch[] = [];
  searchedPartNumber = "";
  isAdded = false;
  manufacturers: Manufacturer[] = [];
  redoSearch: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  currentPage = 1;
  readonly pageSize = 10;

  constructor(@Inject(APP_TYPE) private app: AppType) {}

  private get paginationMatches(): Z2DataMatch[] {
    return Array.isArray(this.app.manufacturerMatching.manufacturer.matches)
      ? this.app.manufacturerMatching.manufacturer.matches
      : [];
  }

  get totalResults() {
    return this.paginationMatches.length;
  }

  get totalPages() {
    return Math.max(1, Math.ceil(this.totalResults / this.pageSize));
  }

  get displayedCurrentPage() {
    return Math.min(this.currentPage, this.totalPages);
  }

  get paginatedMatches() {
    const startIndex = (this.displayedCurrentPage - 1) * this.pageSize;
    return this.paginationMatches.slice(startIndex, startIndex + this.pageSize);
  }

  get displayedResultCount() {
    return this.paginatedMatches.length;
  }

  get hasPreviousPage() {
    return this.displayedCurrentPage > 1;
  }

  get hasNextPage() {
    return this.displayedCurrentPage < this.totalPages;
  }

  async getSearchResults(results: Z2DataMatch[]) {
    this.app.manufacturerMatching.manufacturer.matches = [];
    this.matches = [];
    this.resetPagination();
    let z2DataMatches: Z2DataMatch[] = [];
    const manufacturer = this.app.manufacturerMatching.manufacturer;

    if (!manufacturer.matches) {
      manufacturer.matches = [];
    }

    results.forEach((match: Z2DataMatch) => {
      this.normalizeReachStatus(match);
      match.LifecycleStatus = ManufacturerService.parseStatus(
        match.LifecycleStatus
      );
      match.RoHSStatus = ManufacturerService.parseRoHS(match.RoHSStatus);
      z2DataMatches.push(match as Z2DataMatch);

      manufacturer.searchResults = true;
      if (manufacturer.matches != null) {
        manufacturer.matches = z2DataMatches;
        this.matches = manufacturer.matches;
        this.matchesCopy = manufacturer.matches;
      }
    });
  }

  resetPagination() {
    this.currentPage = 1;
  }

  goToPreviousPage() {
    if (!this.hasPreviousPage) {
      return;
    }

    this.currentPage = this.displayedCurrentPage - 1;
  }

  goToNextPage() {
    if (!this.hasNextPage) {
      return;
    }

    this.currentPage = this.displayedCurrentPage + 1;
  }

  newCase(doc: Z2DataMatch, searchedCpn: string) {
    this.selectedMatch = doc;
    this.selectedMatch.type = "z2DataMatch";
    sessionStorage.setItem("se_filter_cpn", searchedCpn);
    // clear all variables that could have fields info about a thread
    this.app.pcn.pcn = null;
    this.app.pcn.id = null;
    this.app.routing.navigateNewThread();
  }

  setFields() {
    return this.setZ2DataFields();
  }

  setZ2DataFields() {
    const model = doc2Model("manufacturer", {} as Manufacturer);
    const selectedManufacturer = new Z2DataMatch();
    Object.keys(this.app.manufacturer.selectedManufacturer).forEach((key) => {
      selectedManufacturer[key] =
        this.app.manufacturer.selectedManufacturer[key];
    });
    model["manufacturer.name"] = selectedManufacturer.Manufacturer;
    model["manufacturer.nameRaw"] = selectedManufacturer.Manufacturer;
    model["manufacturer.manufacturerPartNumber"] = selectedManufacturer.MPN;
    model["manufacturer.manufacturerPartNumberRaw"] = selectedManufacturer.MPN;
    model["manufacturer.manufacturerPartDescription"] =
      selectedManufacturer.Description;
    model["manufacturer.manufacturerPartDescriptionRaw"] =
      selectedManufacturer.Description;
    model["manufacturer.z2DataId"] = selectedManufacturer.PartID.toString();
    model["manufacturer.datasheet"] = selectedManufacturer.Datasheet;
    model["manufacturer.partNumber"] =
      this.app.model["manufacturer.partNumber"];
    model["manufacturer.obsolescenceStatus"] =
      selectedManufacturer.LifecycleStatus;
    model["manufacturer.euRoHS"] = selectedManufacturer.RoHS;
    model["manufacturer.reachStatus"] =
      this.getReachStatus(selectedManufacturer);
    model["manufacturer.estimatedYearsToEOL"] = Number(
      selectedManufacturer.EstimatedYearsToEOL
    );
    return model;
  }

  private normalizeReachStatus(match: Z2DataMatch) {
    match.ReachStatus = this.getReachStatus(match);
  }

  private getReachStatus(match: Z2DataMatch) {
    return (
      match.ReachStatus ||
      match.REACHStatus ||
      match.ReachSatus ||
      match.ComplianceDetails?.ReachStatus ||
      match.ComplianceDetails?.REACHStatus ||
      ""
    );
  }
}
